import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as patientApi from "@/api/patient.api";
import { mutationMeta } from "@/lib/query-client";
import type { CreatePatientRequest, UpdatePatientRequest } from "@/types";

export interface PatientListParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  phone?: string;
  ssn?: string;
  dob?: string;
  external_id?: string;
  date_from?: string;
  date_to?: string;
  primary_provider_id?: string;
  /** "private", "corporate", or a corporate client id. */
  paid_by?: string;
}

export interface FindPatientParams {
  name?: string;
  phone?: string;
  ssn?: string;
  dob?: string;
  external_id?: string;
  mrn?: string;
}

export function usePatients(params?: PatientListParams) {
  return useQuery({
    queryKey: ["patients", params],
    queryFn: () => patientApi.listPatients(params),
    placeholderData: keepPreviousData,
  });
}

export function usePatient(id: string | undefined) {
  return useQuery({
    queryKey: ["patients", id],
    queryFn: () => patientApi.getPatient(id!),
    enabled: !!id,
  });
}

export function useCreatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreatePatientRequest) => patientApi.createPatient(data),
    meta: mutationMeta.silentSuccess,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["patients"] }),
  });
}

export function useUpdatePatient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePatientRequest }) =>
      patientApi.updatePatient(id, data),
    meta: mutationMeta.updated("Patient"),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["patients", variables.id] });
      // Avatars on the schedule, tracker and dashboards read this cache.
      if (variables.data.photo_url !== undefined) {
        queryClient.setQueryData(["patient-photo", variables.id], variables.data.photo_url ?? "");
      }
    },
  });
}

export function useSearchPatients(query: string) {
  return useQuery({
    queryKey: ["patients", "search", query],
    queryFn: () => patientApi.searchPatients(query),
    enabled: query.length >= 3,
  });
}

export function useFindPatients(params: FindPatientParams, enabled: boolean) {
  const hasAnyParam = Object.values(params).some((v) => v && v.trim() !== "");
  return useQuery({
    queryKey: ["patients", "find", params],
    queryFn: () => patientApi.findPatients(params),
    enabled: enabled && hasAnyParam,
  });
}

/** Coded demographic value sets - static reference data, cached for the session. */
export function useDemographicValueSets() {
  return useQuery({
    queryKey: ["demographics", "value-sets"],
    queryFn: () => patientApi.getDemographicValueSets(),
    staleTime: Infinity,
    gcTime: Infinity,
  });
}
